import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { profileApi } from "@/api";
import { resolveCustomerAssetUrl } from "@/lib/asset";
import { auth } from "@/lib/auth";
import { buildLoginRedirect } from "@/lib/navigation";
import { RequestError } from "@/lib/request";

const GENDERS = [
  { label: "先生", value: 1 },
  { label: "女士", value: 2 },
  { label: "保密", value: 0 },
];

function formatCreateTime(value?: string) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "--";
  }
  const normalized = raw.replace("T", " ");
  return normalized.length >= 19 ? normalized.slice(0, 19) : normalized;
}

function resolveGenderLabel(value?: number) {
  return GENDERS.find((item) => item.value === Number(value))?.label || "保密";
}

function resolveGenderValue(label: string) {
  return GENDERS.find((item) => item.label === label)?.value ?? 0;
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

export default function ProfileEditPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const authUser = auth.getUserInfo();
  const [name, setName] = useState(authUser?.nickname || "");
  const [gender, setGender] = useState("保密");
  const [genderSheetOpen, setGenderSheetOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [avatarUrl, setAvatarUrl] = useState(
    resolveCustomerAssetUrl(String(authUser?.avatar || "")) ||
    "https://readdy.ai/api/search-image?query=young%20stylish%20asian%20woman%20natural%20warm%20smile%20portrait%20minimal%20clean%20white%20background%20soft%20studio%20lighting%20fresh%20appearance&width=160&height=160&seq=profile_avatar_2026&orientation=squarish",
  );
  const [nameEditing, setNameEditing] = useState(false);
  const [mobile, setMobile] = useState(authUser?.mobile || "");
  const [createTime, setCreateTime] = useState("--");

  const redirectToLogin = () => {
    navigate(buildLoginRedirect(`${location.pathname}${location.search}`), { replace: true });
  };

  useEffect(() => {
    let cancelled = false;

    const loadUserProfile = async () => {
      if (!auth.getToken()) {
        redirectToLogin();
        return;
      }

      try {
        const userInfo = await profileApi.getUserInfo();
        if (cancelled) {
          return;
        }

        const nextName = String(userInfo.nickname || authUser?.nickname || "").trim();
        const nextAvatar =
          resolveCustomerAssetUrl(String(userInfo.avatar || authUser?.avatar || "")) ||
          "https://readdy.ai/api/search-image?query=young%20stylish%20asian%20woman%20natural%20warm%20smile%20portrait%20minimal%20clean%20white%20background%20soft%20studio%20lighting%20fresh%20appearance&width=160&height=160&seq=profile_avatar_2026&orientation=squarish";
        const nextMobile = String(userInfo.mobile || authUser?.mobile || "").trim();
        const nextGender = resolveGenderLabel(userInfo.sex);

        setName(nextName);
        setAvatarUrl(nextAvatar);
        setMobile(nextMobile);
        setGender(nextGender);
        setCreateTime(formatCreateTime(userInfo.create_time || userInfo.createTime));
        auth.setUserInfo({
          id: userInfo.id || authUser?.id,
          nickname: nextName || authUser?.nickname,
          avatar: String(userInfo.avatar || authUser?.avatar || ""),
          mobile: nextMobile || authUser?.mobile,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (error instanceof RequestError && error.code === 1001) {
          redirectToLogin();
          return;
        }
        setPageError(getErrorMessage(error, "个人信息加载失败，请稍后重试"));
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadUserProfile();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate]);

  const handleSave = async () => {
    if (saving) {
      return;
    }

    if (!auth.getToken()) {
      redirectToLogin();
      return;
    }

    setSaving(true);
    setPageError("");

    try {
      await profileApi.updateUserInfo({
        nickname: name.trim(),
        sex: resolveGenderValue(gender),
        avatar: auth.getUserInfo()?.avatar,
      });
      auth.setUserInfo({
        ...auth.getUserInfo(),
        nickname: name.trim(),
      });
      setSaved(true);
      window.setTimeout(() => {
        setSaved(false);
        navigate("/profile");
      }, 1000);
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      setPageError(getErrorMessage(error, "个人信息保存失败，请稍后重试"));
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = async (file?: File) => {
    if (!file) {
      return;
    }

    if (!auth.getToken()) {
      redirectToLogin();
      return;
    }

    setPageError("");

    try {
      const avatar = await profileApi.uploadAvatar(file);
      setAvatarUrl(resolveCustomerAssetUrl(avatar) || avatar);
      auth.setUserInfo({
        ...auth.getUserInfo(),
        avatar,
      });
    } catch (error) {
      if (error instanceof RequestError && error.code === 1001) {
        redirectToLogin();
        return;
      }
      setPageError(getErrorMessage(error, "头像上传失败，请稍后重试"));
    }
  };

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ background: "#F5F4F1", maxWidth: 480, margin: "0 auto" }}
    >
      <div
        className="flex items-center justify-between px-4 pt-12 pb-4"
        style={{
          background: "linear-gradient(150deg, #FFB07A 0%, #FF8040 55%, #FF6018 100%)",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer"
          style={{ background: "rgba(255,255,255,0.18)" }}
        >
          <i className="ri-arrow-left-s-line text-xl text-white" />
        </button>
        <h1 className="text-base font-bold text-white">个人信息</h1>
        <button
          onClick={() => void handleSave()}
          className="px-4 py-1.5 rounded-full text-sm font-bold cursor-pointer whitespace-nowrap"
          style={{ background: "rgba(255,255,255,0.25)", color: "#fff" }}
        >
          {saving ? "保存中..." : saved ? "已保存 ✓" : "保存"}
        </button>
      </div>

      {pageError ? (
        <div className="mx-3 mt-3 rounded-2xl px-4 py-3 text-sm" style={{ background: "#FFF4EC", color: "#D94A00", border: "1px solid #FFD8BF" }}>
          {pageError}
        </div>
      ) : null}

      <div className="mx-3 mt-4 rounded-2xl overflow-hidden" style={{ background: "#fff" }}>
        <div
          className="flex items-center justify-between px-4 py-4 border-b"
          style={{ borderColor: "#F5F2EE" }}
        >
          <span className="text-sm" style={{ color: "#1A1A1A" }}>头像</span>
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => fileInputRef.current?.click()}>
            <div
              className="w-11 h-11 rounded-full overflow-hidden"
              style={{ border: "2px solid #FFD4A0" }}
            >
              <img
                src={avatarUrl}
                alt="头像"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <i className="ri-arrow-right-s-line text-base" style={{ color: "#C8C0B8" }} />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(event) => void handleAvatarChange(event.target.files?.[0])}
            />
          </div>
        </div>

        <div
          className="flex items-center justify-between px-4 py-4 border-b"
          style={{ borderColor: "#F5F2EE" }}
        >
          <span className="text-sm" style={{ color: "#1A1A1A" }}>昵称</span>
          <div className="flex items-center gap-1.5">
            {nameEditing ? (
              <input
                autoFocus
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value.slice(0, 20))}
                onBlur={() => setNameEditing(false)}
                className="text-sm outline-none text-right bg-transparent"
                style={{ color: "#666", minWidth: 80 }}
              />
            ) : (
              <span
                className="text-sm cursor-pointer"
                style={{ color: "#888" }}
                onClick={() => setNameEditing(true)}
              >
                {loading ? "加载中..." : (name || "未设置")}
              </span>
            )}
            <i className="ri-arrow-right-s-line text-base" style={{ color: "#C8C0B8" }} />
          </div>
        </div>

        <div
          className="flex items-center justify-between px-4 py-4 border-b"
          style={{ borderColor: "#F5F2EE" }}
        >
          <span className="text-sm" style={{ color: "#1A1A1A" }}>账号</span>
          <div className="flex items-center gap-1.5">
            <span className="text-sm" style={{ color: "#888" }}>{mobile || "--"}</span>
            <i className="ri-arrow-right-s-line text-base" style={{ color: "#C8C0B8" }} />
          </div>
        </div>

        <div
          className="flex items-center justify-between px-4 py-4 border-b"
          style={{ borderColor: "#F5F2EE" }}
        >
          <span className="text-sm" style={{ color: "#1A1A1A" }}>性别</span>
          <button
            onClick={() => setGenderSheetOpen(true)}
            className="flex items-center gap-1.5 cursor-pointer"
          >
            <span className="text-sm" style={{ color: "#888" }}>{gender || "未设置"}</span>
            <i className="ri-arrow-right-s-line text-base" style={{ color: "#C8C0B8" }} />
          </button>
        </div>

        <div
          className="flex items-center justify-between px-4 py-4 border-b"
          style={{ borderColor: "#F5F2EE" }}
        >
          <span className="text-sm" style={{ color: "#1A1A1A" }}>手机号</span>
          <div className="flex items-center gap-1.5">
            <span className="text-sm" style={{ color: mobile ? "#888" : "#BBAAA0" }}>{mobile || "未绑定手机号"}</span>
            <i className="ri-arrow-right-s-line text-base" style={{ color: "#C8C0B8" }} />
          </div>
        </div>

        <div className="flex items-center justify-between px-4 py-4">
          <span className="text-sm" style={{ color: "#1A1A1A" }}>注册时间</span>
          <span className="text-sm" style={{ color: "#BBAAA0" }}>{createTime}</span>
        </div>
      </div>

      <div className="mx-3 mt-3 rounded-2xl overflow-hidden" style={{ background: "#fff" }}>
        {[
          { icon: "ri-file-text-line", label: "服务协议", color: "#FF6018", bg: "#FFF4EC", type: "service" },
          { icon: "ri-shield-user-line", label: "隐私政策", color: "#17A87B", bg: "#EDFBF4", type: "privacy" },
        ].map((item, index) => (
          <button
            key={item.label}
            onClick={() => navigate(`/agreement?type=${item.type}`)}
            className={`w-full flex items-center gap-3 px-4 py-4 cursor-pointer active:opacity-70 ${index === 0 ? "border-b" : ""}`}
            style={{ borderColor: "#F5F2EE" }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: item.bg }}
            >
              <i className={`${item.icon} text-sm`} style={{ color: item.color }} />
            </div>
            <span className="flex-1 text-sm font-medium text-left" style={{ color: "#1A1A1A" }}>
              {item.label}
            </span>
            <i className="ri-arrow-right-s-line text-base" style={{ color: "#C8C0B8" }} />
          </button>
        ))}
      </div>

      <div className="mx-3 mt-3">
        <button
          onClick={() => {
            auth.clearSession();
            navigate("/");
          }}
          className="w-full py-3.5 rounded-2xl text-sm font-bold cursor-pointer whitespace-nowrap"
          style={{
            background: "#FFF0EE",
            color: "#E83A2A",
            border: "1px solid #FFDAD7",
          }}
        >
          退出登录
        </button>
      </div>

      {genderSheetOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ maxWidth: 480, margin: "0 auto", left: "50%", transform: "translateX(-50%)" }}
        >
          <div className="absolute inset-0 bg-black/40" onClick={() => setGenderSheetOpen(false)} />
          <div className="relative w-full bg-white rounded-t-3xl pb-10 z-10">
            <div
              className="w-10 h-1 rounded-full mx-auto mt-3 mb-4"
              style={{ background: "#E0D8D0" }}
            />
            <div className="px-5 mb-4 flex items-center justify-between">
              <h3 className="text-base font-bold" style={{ color: "#1A1A1A" }}>选择性别</h3>
              <button onClick={() => setGenderSheetOpen(false)} className="w-7 h-7 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-lg" style={{ color: "#999" }} />
              </button>
            </div>
            <div className="px-5 space-y-2">
              {GENDERS.map((item) => (
                <button
                  key={item.label}
                  onClick={() => {
                    setGender(item.label);
                    setGenderSheetOpen(false);
                  }}
                  className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl cursor-pointer"
                  style={{
                    background: gender === item.label ? "#FFF4EC" : "#FAFAF8",
                    border: `1px solid ${gender === item.label ? "#FFD4A0" : "#F0EAE0"}`,
                  }}
                >
                  <span className="text-sm font-medium" style={{ color: gender === item.label ? "#FF6018" : "#1A1A1A" }}>
                    {item.label}
                  </span>
                  {gender === item.label ? (
                    <i className="ri-check-line text-base" style={{ color: "#FF6018" }} />
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
